import { X, Navigation, DoorOpen } from "lucide-react";
import { useState, useRef, useEffect, useMemo, useCallback, forwardRef, useImperativeHandle } from "react";
import { useMediaQuery } from "@mantine/hooks";
import { Box, Flex, Group, Stack, Button, Text } from "@mantine/core";
import { FloorplanViewer, type FloorplanViewerHandle } from "@nodus/floorplan";
import { NavigationRoute } from "./NavigationRoute";
import { formatRouteSummary } from "@/lib/route-summary";
import { InstructionMapCutout } from "./directions/InstructionMapCutout";
import { routeByFloorplan } from "./directions/geometry";
import { useDirectionsPlan } from "./directions/useDirectionsPlan";
import type { NavigateResponse, NavigationSegment, WalkSegment, NavigateInstruction, NearestExitResponse } from "@/lib/api/client";

interface MultiFloorNavigationProps {
  /** A room route, or a nearest-exit route (`NearestExitResponse`, which adds `exit`). */
  result: NavigateResponse | NearestExitResponse;
  onClose: () => void;
}

export interface WalkSegmentMapHandle {
  zoomToPoints: (points: [number, number][]) => void;
}

const WalkSegmentMap = forwardRef<WalkSegmentMapHandle, { segment: WalkSegment; highlightPath?: [number, number][]; focusPoint?: [number, number] }>(
  function WalkSegmentMap({ segment, highlightPath, focusPoint }, ref) {
    const viewerRef = useRef<FloorplanViewerHandle>(null);
    // Rooms and doors worked out once per plan (shared with the step cut-outs), not per render.
    const plan = useDirectionsPlan(segment.floorplan_id);
    const zoomTo = useCallback((points: [number, number][], padding = 120) => {
      if (!points.length) return;
      const xs = points.map((p) => p[0]), ys = points.map((p) => p[1]);
      const cx = (Math.min(...xs) + Math.max(...xs)) / 2, cy = (Math.min(...ys) + Math.max(...ys)) / 2;
      const ext = Math.max(Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys), 100);
      viewerRef.current?.zoomToRect(cx - ext / 2, cy - ext / 2, cx + ext / 2, cy + ext / 2, padding);
    }, []);
    useImperativeHandle(ref, () => ({ zoomToPoints: (pts) => zoomTo(pts) }), [zoomTo]);
    useEffect(() => {
      if (!segment.path.length) return;
      const t = setTimeout(() => zoomTo(segment.path, 100), 300);
      return () => clearTimeout(t);
    }, [segment.path, plan, zoomTo]);
    if (!segment.svg_content) return null;
    return (
      <Box h="100%" w="100%" pos="relative">
        <FloorplanViewer ref={viewerRef} svg={segment.svg_content} rooms={plan?.rooms ?? []} scale={plan?.scale} floorFurniture={plan?.furniture ?? []} doors={plan?.doors ?? []} hidePanel
          svgOverlay={<g>
            <NavigationRoute path={segment.path} distanceMeters={segment.distance_meters} />
            {highlightPath && highlightPath.length >= 2 && (
              <polyline points={highlightPath.map((p) => p.join(",")).join(" ")} fill="none" stroke="#ef4444" strokeWidth={5} strokeLinecap="round" strokeLinejoin="round" strokeDasharray="8,5">
                <animate attributeName="stroke-dashoffset" from="26" to="0" dur="1s" repeatCount="indefinite" />
              </polyline>
            )}
            {focusPoint && <circle cx={focusPoint[0]} cy={focusPoint[1]} r={7} fill="#ef4444" stroke="#fff" strokeWidth={2.5} />}
          </g>}
        />
      </Box>
    );
  },
);

function InstructionsList({ instructions, segments }: { instructions: NavigateInstruction[]; segments: NavigationSegment[] }) {
  const fullPathByFp = useMemo(() => routeByFloorplan(segments), [segments]);

  return (
    <Stack px="md" py="sm" gap="xs">
      {instructions.map((inst, i) => (
        <Box key={i}>
          <Box py="xs">
            <Text fz="lg" fw={(inst.type === "depart" || inst.type === "arrive") ? 600 : 400}>
              {inst.text}
            </Text>
          </Box>
          {inst.floorplan_id && (inst.path_portion?.length || inst.focus) && (
            <InstructionMapCutout instruction={inst} fullPath={fullPathByFp.get(inst.floorplan_id)} />
          )}
        </Box>
      ))}
    </Stack>
  );
}

export function MultiFloorNavigation({ result, onClose }: MultiFloorNavigationProps) {
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const walkSegments = result.segments.filter((s): s is WalkSegment => s.type === "walk");
  const [activeSegmentIdx, setActiveSegmentIdx] = useState(0);
  const [activeInstructionIdx, setActiveInstructionIdx] = useState<number | null>(null);
  const activeWalkSegment = walkSegments[activeSegmentIdx] ?? walkSegments[0];
  const activeInstruction = activeInstructionIdx != null ? result.instructions[activeInstructionIdx] : null;
  const mapRef = useRef<WalkSegmentMapHandle>(null);

  const handleInstructionClick = useCallback((inst: NavigateInstruction, idx: number) => {
    setActiveInstructionIdx(idx);
    if (inst.floorplan_id) {
      const floorIdx = walkSegments.findIndex((s) => s.floorplan_id === inst.floorplan_id);
      if (floorIdx >= 0 && floorIdx !== activeSegmentIdx) {
        setActiveSegmentIdx(floorIdx);
        setTimeout(() => {
          const pts = inst.path_portion ?? (inst.focus ? [inst.focus] : null);
          if (pts) mapRef.current?.zoomToPoints(pts as [number, number][]);
        }, 500);
        return;
      }
    }
    const pts = inst.path_portion ?? (inst.focus ? [inst.focus] : null);
    if (pts) mapRef.current?.zoomToPoints(pts as [number, number][]);
  }, [walkSegments, activeSegmentIdx]);

  return (
    <Flex pos="absolute" style={{ inset: 0, zIndex: 50 }} bg="var(--mantine-color-body)" direction="column">
      {/* Header */}
      <Group justify="space-between" px="md" py="xs" style={{ borderBottom: "1px solid var(--mantine-color-default-border)", background: "var(--mantine-color-default-hover)", flexShrink: 0 }}>
        <Group gap="xs" miw={0}>
          {"exit" in result ? (
            <DoorOpen size={16} aria-label="Route to an exit" style={{ color: "var(--mantine-color-green-6)", flexShrink: 0 }} />
          ) : (
            <Navigation size={16} style={{ color: "var(--mantine-primary-color-filled)", flexShrink: 0 }} />
          )}
          <Box miw={0}>
            <Text fz="sm" fw={500} truncate="end">
              {walkSegments[0]?.start_label} &rarr; {walkSegments[walkSegments.length - 1]?.end_label}
            </Text>
            <Text fz="xs" c="dimmed">
              {formatRouteSummary(result)}
            </Text>
          </Box>
        </Group>
        <Button variant="subtle" size="compact-sm" onClick={onClose} px={4}>
          <X size={20} />
        </Button>
      </Group>

      {isDesktop ? (
        <Flex flex={1} mih={0}>
          {/* Map. miw 0: a flex item's minimum width is its content's, and the plan's <svg> is as wide as its
              viewBox (thousands of px), which pushed the Directions sidebar off-screen. */}
          <Box flex={1} miw={0} pos="relative" style={{ overflow: "hidden" }}>
            {activeWalkSegment && (
              <WalkSegmentMap ref={mapRef} segment={activeWalkSegment}
                highlightPath={activeInstruction?.path_portion as [number, number][] | undefined}
                focusPoint={activeInstruction?.focus as [number, number] | undefined}
              />
            )}
            {walkSegments.length > 1 && (
              <Group pos="absolute" top={8} left={8} gap={4} style={{ zIndex: 10 }}>
                {walkSegments.map((seg, i) => (
                  <Button key={i} size="compact-xs" variant={i === activeSegmentIdx ? "filled" : "default"} onClick={() => setActiveSegmentIdx(i)}>
                    {seg.floorplan_name}
                  </Button>
                ))}
              </Group>
            )}
          </Box>

          {/* Sidebar */}
          <Box w={300} style={{ borderLeft: "1px solid var(--mantine-color-default-border)", overflowY: "auto", flexShrink: 0 }}>
            <Box px="sm" py="xs" style={{ borderBottom: "1px solid var(--mantine-color-default-border)", background: "var(--mantine-color-default-hover)" }}>
              <Text fz={11} fw={500} c="dimmed" tt="uppercase" style={{ letterSpacing: "0.05em" }}>Directions</Text>
            </Box>
            {result.instructions.map((inst, i) => (
              <Box
                key={i}
                component="button"
                onClick={() => handleInstructionClick(inst, i)}
                style={{
                  width: "100%", textAlign: "left", padding: "8px 12px", border: "none", cursor: "pointer",
                  borderBottom: "1px solid rgba(128,128,128,0.15)", transition: "background 150ms",
                  background: activeInstructionIdx === i ? "var(--mantine-primary-color-light)" : "transparent",
                  borderLeft: activeInstructionIdx === i ? "2px solid var(--mantine-primary-color-filled)" : "2px solid transparent",
                  color: "var(--mantine-color-text)",
                }}
              >
                <Text fz="sm" fw={(inst.type === "depart" || inst.type === "arrive") ? 500 : 400}>
                  {inst.text}
                </Text>
              </Box>
            ))}
          </Box>
        </Flex>
      ) : (
        <Box flex={1} style={{ overflowY: "auto" }}>
          <InstructionsList instructions={result.instructions} segments={result.segments} />
        </Box>
      )}
    </Flex>
  );
}
